import React, { useState } from 'react';
import {
  LayoutDashboard, UsersRound, Handshake, Package, ListTodo,
  BarChart3, Settings, Bell, Menu, ChevronLeft, ChevronRight, LogOut, Building2,
  Star, Compass,
} from 'lucide-react';
import { Avatar } from './ui.jsx';
import { money } from '../data.js';
import scodacLogo from '../../Scodac Logo Approved 1 new one (2).png';
import scodacFavicon from '../../scodac-favicon.png';

function buildNav(dealRecords, partnerRecords) {
  const openDeals = dealRecords.filter((d) => !['Won', 'Lost'].includes(d.stage)).length;
  return [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'leads', label: 'Leads & Pipeline', icon: UsersRound, badge: openDeals || null },
    { key: 'partners', label: 'Partners', icon: Handshake, badge: partnerRecords.length || null },
    { key: 'accounts', label: 'Accounts', icon: Building2 },
    { key: 'products', label: 'Products', icon: Package },
    { key: 'tasks', label: 'Tasks & Activities', icon: ListTodo },
    { key: 'reports', label: 'Reports', icon: BarChart3 },
  ];
}

const VIEWS = [
  { label: 'At-risk partners', nav: 'partners' },
];

export function Sidebar({ active, onNavigate, collapsed, setCollapsed, mobileOpen, setMobileOpen, currentUser, onLogout, deals: dealRecords = [], partners: partnerRecords = [], accounts: accountRecords = [] }) {
  const favorites = [
    ...accountRecords.slice(0, 2).map((account) => ({ label: account.name, nav: 'accounts' })),
    ...partnerRecords.slice(0, 1).map((partner) => ({ label: partner.name, nav: 'partners' })),
  ];
  const targetPipeline = 2000000;
  const currentPipeline = dealRecords.filter((d) => !['Won', 'Lost'].includes(d.stage)).reduce((s, d) => s + d.value, 0);
  const pct = Math.min(100, Math.round((currentPipeline / targetPipeline) * 100));

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img className="sidebar-logo-image" src={collapsed ? scodacFavicon : scodacLogo} alt="SCODAC" />
          </div>
          {!collapsed && (
            <button className="sidebar-toggle-tab" onClick={() => setCollapsed(true)}>
              <ChevronLeft size={14} />
            </button>
          )}
        </div>

        {collapsed && (
          <button className="sidebar-toggle-tab" style={{ margin: '0 auto 14px' }} onClick={() => setCollapsed(false)}>
            <ChevronRight size={14} />
          </button>
        )}

        <nav className="sidebar-nav">
          <div className="sidebar-nav-section">
            {!collapsed && <div className="sidebar-nav-label">Workspace</div>}
            {buildNav(dealRecords, partnerRecords).map((item) => (
              <button
                key={item.key}
                className={`nav-item ${collapsed ? 'nav-item--collapsed' : ''} ${active === item.key ? 'active' : ''}`}
                onClick={() => { onNavigate(item.key); setMobileOpen(false); }}
                title={collapsed ? item.label : undefined}
              >
                <span className="nav-icon"><item.icon size={18} /></span>
                {!collapsed && <span className="nav-label">{item.label}</span>}
                {!collapsed && item.badge && <span className={`nav-badge nav-badge--${item.badgeTone || 'neutral'}`}>{item.badge}</span>}
              </button>
            ))}
          </div>

          {!collapsed && favorites.length > 0 && (
            <>
              <div className="sidebar-divider" />
              <div className="sidebar-nav-section">
                <div className="sidebar-nav-label">
                  <span>Favorites</span>
                  <button><Star size={12} /></button>
                </div>
                {favorites.map((f) => (
                  <button key={f.label} className="nav-item nav-item--sub" onClick={() => onNavigate(f.nav)}>
                    <span className="nav-icon"><Star size={15} /></span>
                    <span className="nav-label">{f.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {!collapsed && (
            <>
              <div className="sidebar-divider" />
              <div className="sidebar-nav-section">
                <div className="sidebar-nav-label">
                  <span>Views</span>
                  <button><Compass size={12} /></button>
                </div>
                {VIEWS.map((v) => (
                  <button key={v.label} className="nav-item nav-item--sub" onClick={() => onNavigate(v.nav)}>
                    <span className="nav-icon"><Compass size={15} /></span>
                    <span className="nav-label">{v.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </nav>

        {!collapsed && (
          <div className="sidebar-goal">
            <div className="sidebar-goal-top">
              <span className="sidebar-goal-label">Quarter pipeline goal</span>
              <span className="sidebar-goal-pct">{pct}%</span>
            </div>
            <div className="progress-track"><div className="progress-bar progress-bar--info" style={{ width: `${pct}%` }} /></div>
            <div className="sidebar-goal-desc">{money(currentPipeline)} of {money(targetPipeline)} target open pipeline.</div>
            <button className="sidebar-goal-btn">View forecast</button>
          </div>
        )}

        <div className="sidebar-footer">
          <button
            className={`nav-item ${collapsed ? 'nav-item--collapsed' : ''} ${active === 'settings' ? 'active' : ''}`}
            style={{ marginBottom: 6 }}
            onClick={() => { onNavigate('settings'); setMobileOpen(false); }}
            title={collapsed ? 'Settings' : undefined}
          >
            <span className="nav-icon"><Settings size={18} /></span>
            {!collapsed && <span className="nav-label">Settings</span>}
          </button>
          <div className="sidebar-client-badge">
            <Avatar name={currentUser.name} color={currentUser.color} />
            {!collapsed && (
              <div className="client-info">
                <div className="client-name">{currentUser.name}</div>
                <div className="client-role">{currentUser.role}</div>
              </div>
            )}
            {!collapsed && (
              <button className="sidebar-logout-btn" onClick={onLogout} title="Log out">
                <LogOut size={15} />
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

const navLabel = { dashboard: 'Dashboard', leads: 'Leads & Pipeline', partners: 'Partners', accounts: 'Accounts', products: 'Products', tasks: 'Tasks & Activities', reports: 'Reports', settings: 'Settings' };

export function Header({
  active, onMenu, currentUser, workspaceName = 'SCODAC',
  notifications = [], onReloadNotifications, onMarkNotificationRead, onMarkAllNotificationsRead,
}) {
  const [showNotif, setShowNotif] = useState(false);
  const unread = notifications.filter((n) => n.unread).length;

  const toggleNotif = () => {
    setShowNotif((v) => {
      if (!v) onReloadNotifications?.();
      return !v;
    });
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button className="btn-icon header-hamburger" onClick={onMenu}><Menu size={18} /></button>
        <div className="header-breadcrumb">
          <span className="breadcrumb-root">{workspaceName}</span>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{navLabel[active] || 'Dashboard'}</span>
        </div>
      </div>
      <div className="header-actions">
        <div style={{ position: 'relative' }}>
          <button className="btn-icon" onClick={toggleNotif}>
            <Bell size={16} />
            {unread > 0 && <span className="notif-dot" />}
          </button>
          {showNotif && (
            <div className="notif-popover">
              {unread > 0 && (
                <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', marginBottom: 6 }} onClick={onMarkAllNotificationsRead}>
                  Mark all as read
                </button>
              )}
              <div className="notif-list">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`notif-item ${n.unread ? 'notif-item--unread' : ''}`}
                    onClick={() => n.unread && onMarkNotificationRead?.(n.id)}
                    style={{ cursor: n.unread ? 'pointer' : 'default' }}
                  >
                    <span className={`notif-icon-wrap badge--${n.tone}`}><Bell size={13} /></span>
                    <div className="activity-content">
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-body">{n.body}</div>
                      <div className="notif-time">{n.time}</div>
                    </div>
                    {n.unread && <span className="notif-unread-dot" />}
                  </div>
                ))}
                {notifications.length === 0 && <div className="table-empty">No notifications yet.</div>}
              </div>
            </div>
          )}
        </div>
        <Avatar name={currentUser.name} color={currentUser.color} size="sm" />
      </div>
    </header>
  );
}
